import type {
  CarePlan,
  Condition,
  Goal,
  ServiceRequest,
} from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import { Caption, Icon, Stack, Tag, Text, cn } from '@eluve/components';
import {
  icd10Code,
  isPendingReview as isConditionPendingReview,
  isRetracted as isConditionRetracted,
  isRefuted,
} from '@eluve/fhir-conditions';
import {
  formatGoalTarget,
  isPendingReview as isGoalPendingReview,
  isRetracted as isGoalRetracted,
  latestTrackedValue,
  trackedObservationGroup,
  trackingLabel,
} from '@eluve/fhir-goals';
import {
  type GroupedObservations,
  TrendSparkline,
  formatMeasureValue,
} from '@eluve/fhir-observations';
import {
  FhirStatusTag,
  GoalAchievementTag,
  formatFhirDate,
  goalProgress,
  resourceDisplayName,
} from '@eluve/fhir-renderer';

import {
  type LinkedCondition,
  type LinkedGoal,
  type PhaseStep,
  carePlanToken,
  currentPhase,
  linkedConditions,
  linkedGoals,
  planEndDate,
  planPhaseSteps,
  planProgress,
} from '../plan-list';

/**
 * One active care plan of the Overview-tab Care Plans section (ENG-5369,
 * Rev7): a 48px header toggling between the collapsed summary (phase pills +
 * week progress split, treated-condition and linked-goal pills) and the
 * expanded detail (phase stepper, dot-aligned dated progress, then full
 * condition and goal rows mirroring the dedicated cards — resources outside
 * the loaded windows degrade to display-only rows). Conditions and goals
 * cross-link into the sibling sections' URL-driven drawers — the card owns
 * no drawer of its own.
 */
export type CarePlanCardProps = {
  plan: CarePlan;
  /** The loaded request window phase states resolve from (spine sticks). */
  serviceRequests: ServiceRequest[];
  /** The loaded condition window `CarePlan.addresses` resolve against. */
  conditions: Condition[];
  /** The loaded goal window `CarePlan.goal` references resolve against. */
  goals: Goal[];
  /** The grouped observation window goal rows resolve trends from. */
  grouped: GroupedObservations | null;
  /** Today as an ISO date — injected so week math stays pure/testable. */
  todayIsoDate: string;
  expanded: boolean;
  onToggle: (token: string) => void;
  /** Open the Conditions drawer (`?condition=<id>`, owned by that section). */
  onOpenCondition: (token: string) => void;
  /** Open the Goals drawer (`?goal=<id>`, owned by that section). */
  onOpenGoal: (token: string) => void;
};

const ProgressBar: React.FC<{ percent: number }> = ({ percent }) => (
  <div className="h-1 overflow-hidden rounded-full bg-bg-secondary">
    <div
      className="h-full rounded-full bg-text-primary"
      style={{ width: `${percent}%` }}
    />
  </div>
);

const PhasePills: React.FC<{ steps: PhaseStep[] }> = ({ steps }) => (
  <div className="flex gap-1" data-testid="care-plan-phase-pills">
    {steps.map((step, index) => (
      <span
        key={`${step.title}-${index}`}
        aria-hidden
        className={cn(
          'h-1 flex-1 rounded-full',
          step.state === 'done'
            ? 'bg-text-primary'
            : step.state === 'current'
              ? 'bg-bg-indigo-strong-primary'
              : 'bg-border-primary',
        )}
      />
    ))}
  </div>
);

const SummaryRows: React.FC<{
  title: string;
  /** Unresolved rows (resource outside the loaded window) render inert. */
  rows: { token: string; display: string; resolved: boolean }[];
  testId: string;
  onOpen: (token: string) => void;
}> = ({ title, rows, testId, onOpen }) => (
  <div>
    <Caption size="3xs" color="tertiary" className="mb-1 px-3">
      {title}
    </Caption>
    <div className="flex flex-wrap gap-1">
      {rows.map((row) =>
        row.resolved ? (
          <button
            key={row.token}
            type="button"
            data-testid={testId}
            onClick={() => onOpen(row.token)}
            className="flex max-w-full items-center rounded-full px-3 py-2 text-left hover:bg-bg-secondary"
          >
            <Text inline size="sm" weight="medium" className="min-w-0 truncate">
              {row.display}
            </Text>
          </button>
        ) : (
          <span
            key={row.token}
            data-testid={testId}
            className="flex max-w-full items-center rounded-full px-3 py-2"
          >
            <Text inline size="sm" weight="medium" className="min-w-0 truncate">
              {row.display}
            </Text>
          </span>
        ),
      )}
    </div>
  </div>
);

const DETAIL_ROW_CLASS =
  'relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary hover:bg-bg-secondary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0';

const DetailSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div>
    <Caption size="3xs" color="tertiary" className="mb-1 px-3">
      {title}
    </Caption>
    <div>{children}</div>
  </div>
);

/**
 * Inert fallback for a resource outside the loaded window: the sibling
 * section's drawer resolves against its own window, so opening it would be
 * a dead click — no chevron, no hover, no handler.
 */
const UnresolvedDetailRow: React.FC<{
  display: string;
  testId: string;
}> = ({ display, testId }) => (
  <div
    data-testid={testId}
    className="pointer-events-none relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary [:hover+&]:before:opacity-0"
  >
    <Text inline size="sm" weight="medium" className="min-w-0 flex-1 truncate">
      {display}
    </Text>
  </div>
);

/** Mirror of the Conditions card's rows over the plan-linked conditions. */
const ConditionDetailRows: React.FC<{
  conditions: LinkedCondition[];
  onOpenCondition: (token: string) => void;
}> = ({ conditions, onOpenCondition }) => (
  <>
    {conditions.map((linked) => {
      const condition = linked.condition;
      if (!condition) {
        return (
          <UnresolvedDetailRow
            key={linked.token}
            display={linked.display}
            testId="care-plan-condition-row"
          />
        );
      }
      const icd10 = icd10Code(condition);
      const status = condition.clinicalStatus?.coding?.[0]?.code;
      const date = formatFhirDate(
        condition.onsetDateTime ?? condition.recordedDate,
      );
      const pending = isConditionPendingReview(condition);
      return (
        <button
          key={linked.token}
          type="button"
          onClick={() => onOpenCondition(linked.token)}
          data-testid="care-plan-condition-row"
          className={DETAIL_ROW_CLASS}
        >
          <div className="min-w-0 flex-1">
            <Stack
              direction="horizontal"
              gap={2}
              align="center"
              fullWidth={false}
              className="min-h-6"
            >
              <Text inline size="sm" weight="medium" className="truncate">
                {resourceDisplayName(condition)}
              </Text>
              {status && status !== 'active' && (
                <FhirStatusTag status={status} />
              )}
              {icd10 && (
                <Tag pill subtle size="xs" color="lightblue" label={icd10} />
              )}
              {pending && (
                <Tag
                  pill
                  subtle
                  size="xs"
                  color="insights"
                  label={isRefuted(condition) ? 'Denial · Draft' : 'Draft'}
                />
              )}
            </Stack>
            {date && (
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="mt-0.5"
              >
                {condition.onsetDateTime
                  ? `Onset: ${date}`
                  : `Recorded: ${date}`}
              </Text>
            )}
          </div>
          <Icon
            name="ChevronRight"
            size="xs"
            className="shrink-0 text-text-tertiary"
          />
        </button>
      );
    })}
  </>
);

/** Mirror of the Goals card's rows over the plan-linked goals. */
const GoalDetailRows: React.FC<{
  goals: LinkedGoal[];
  grouped: GroupedObservations | null;
  onOpenGoal: (token: string) => void;
}> = ({ goals, grouped, onOpenGoal }) => (
  <>
    {goals.map((linked) => {
      const goal = linked.goal;
      if (!goal) {
        return (
          <UnresolvedDetailRow
            key={linked.token}
            display={linked.display}
            testId="care-plan-goal-row"
          />
        );
      }
      const pending = isGoalPendingReview(goal);
      const date = formatFhirDate(goal.startDate ?? goal.statusDate);
      const group = trackedObservationGroup(goal, grouped);
      const label = trackingLabel(goal, group);
      const now = latestTrackedValue(group);
      const target = formatGoalTarget(goal);
      const latestPending = group?.pending[group.pending.length - 1];
      const atRisk = goalProgress(goal)?.color === 'warning';
      const secondary = [
        date
          ? goal.startDate
            ? `Since: ${date}`
            : `Updated: ${date}`
          : undefined,
        label ? `Tracking: ${label}` : undefined,
      ]
        .filter(Boolean)
        .join(' · ');
      return (
        <button
          key={linked.token}
          type="button"
          onClick={() => onOpenGoal(linked.token)}
          data-testid="care-plan-goal-row"
          className={DETAIL_ROW_CLASS}
        >
          <div className="min-w-0 flex-1">
            <Stack
              direction="horizontal"
              gap={2}
              align="center"
              fullWidth={false}
              className="min-h-6"
            >
              <Text inline size="sm" weight="medium" className="truncate">
                {resourceDisplayName(goal)}
              </Text>
              <GoalAchievementTag goal={goal} />
              {pending && (
                <Tag pill subtle size="xs" color="insights" label="Draft" />
              )}
            </Stack>
            {secondary && (
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="mt-0.5"
              >
                {secondary}
              </Text>
            )}
          </div>
          {(now ?? target) && (
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Text
                inline
                size="sm"
                color="tertiary"
                weight="medium"
                className="tabular-nums"
              >
                {now && (
                  <>
                    Now:{' '}
                    <Text
                      inline
                      size="sm"
                      weight="semibold"
                      className={atRisk ? 'text-text-warning' : undefined}
                    >
                      {formatMeasureValue(now.value, group?.unit)}
                    </Text>
                  </>
                )}
                {now && target && ' · '}
                {target && `Target: ${target}`}
              </Text>
              {group && group.series.length + group.pending.length > 1 && (
                <TrendSparkline
                  className={
                    atRisk ? 'text-text-warning' : 'text-text-positive'
                  }
                  points={group.series.map((point) => ({
                    value: point.value,
                    amended: point.amended,
                  }))}
                  proposedValue={
                    latestPending?.valueQuantity?.value ?? undefined
                  }
                />
              )}
            </div>
          )}
          <Icon
            name="ChevronRight"
            size="xs"
            className="shrink-0 text-text-tertiary"
          />
        </button>
      );
    })}
  </>
);

/**
 * The sibling sections drop retracted resources (and reviewed refuted
 * denials) from their lists, so their drawers can never open them — strip
 * the resolved resource so those rows render inert instead of dead-linking.
 */
function openableCondition(linked: LinkedCondition): LinkedCondition {
  const condition = linked.condition;
  const hidden =
    condition &&
    (isConditionRetracted(condition) ||
      (isRefuted(condition) && !isConditionPendingReview(condition)));
  return hidden ? { token: linked.token, display: linked.display } : linked;
}

function openableGoal(linked: LinkedGoal): LinkedGoal {
  return linked.goal && isGoalRetracted(linked.goal)
    ? { token: linked.token, display: linked.display }
    : linked;
}

const STEP_CIRCLE: Record<PhaseStep['state'], string> = {
  done: 'bg-text-primary text-bg-primary',
  current:
    'bg-bg-indigo-strong-primary text-bg-primary ring-4 ring-bg-indigo-subtle-secondary',
  upcoming: 'bg-bg-secondary text-text-tertiary',
};

const PhaseStepper: React.FC<{ steps: PhaseStep[] }> = ({ steps }) => (
  <div className="flex" data-testid="care-plan-phase-stepper">
    {steps.map((step, index) => (
      <div
        key={`${step.title}-${index}`}
        className="relative flex flex-1 flex-col items-center text-center"
      >
        {index < steps.length - 1 && (
          <div
            aria-hidden
            className={cn(
              'absolute top-2.5 left-[calc(50%+0.75rem)] h-0.5 w-[calc(100%-1.5rem)]',
              step.state === 'done' ? 'bg-text-primary' : 'bg-bg-secondary',
            )}
          />
        )}
        <div
          className={cn(
            'relative z-1 flex size-5.5 items-center justify-center rounded-full text-xs font-semibold',
            STEP_CIRCLE[step.state],
          )}
        >
          {step.state === 'done' ? (
            <Icon name="Check" size="xs" strokeWidth={3} />
          ) : (
            index + 1
          )}
        </div>
        <Text
          size="sm"
          weight="medium"
          className={cn(
            'mt-2',
            step.state === 'current'
              ? 'text-text-indigo'
              : step.state === 'done'
                ? 'text-text-primary'
                : 'text-text-tertiary',
          )}
        >
          {step.title}
        </Text>
        {step.weeksLabel && (
          <Text size="xs" color="tertiary" className="mt-0.5">
            {step.weeksLabel}
          </Text>
        )}
      </div>
    ))}
  </div>
);

const DatedProgress: React.FC<{
  started?: string;
  end?: string;
  weekNow?: number;
  weekTotal?: number;
  percent?: number;
}> = ({ started, end, weekNow, weekTotal, percent }) => (
  <>
    <Stack
      direction="horizontal"
      gap={4}
      align="baseline"
      justify="between"
      className="mb-2"
    >
      {started && (
        <Text inline size="xs" color="tertiary">
          Started:{' '}
          <Text inline size="xs">
            {formatFhirDate(started)}
          </Text>
        </Text>
      )}
      {weekNow !== undefined && weekTotal !== undefined && (
        <Text inline size="xs" color="tertiary">
          Week:{' '}
          <Text inline size="xs">
            {weekNow} of {weekTotal}
          </Text>
        </Text>
      )}
      {end && (
        <Text inline size="xs" color="tertiary">
          Est. end:{' '}
          <Text inline size="xs">
            {formatFhirDate(end)}
          </Text>
        </Text>
      )}
    </Stack>
    {percent !== undefined && <ProgressBar percent={percent} />}
  </>
);

export const CarePlanCard: React.FC<CarePlanCardProps> = ({
  plan,
  serviceRequests,
  conditions,
  goals,
  grouped,
  todayIsoDate,
  expanded,
  onToggle,
  onOpenCondition,
  onOpenGoal,
}) => {
  const token = carePlanToken(plan);
  const steps = useMemo(
    () => planPhaseSteps(plan, serviceRequests),
    [plan, serviceRequests],
  );
  const treating = useMemo(
    () => linkedConditions(plan, conditions).map(openableCondition),
    [plan, conditions],
  );
  const linked = useMemo(
    () => linkedGoals(plan, goals).map(openableGoal),
    [plan, goals],
  );
  const progress = useMemo(
    () => planProgress(plan, todayIsoDate),
    [plan, todayIsoDate],
  );
  const now = currentPhase(steps);
  const phaseNumber = now ? steps.indexOf(now) + 1 : null;
  const treatingTitle = `Treating: ${treating.length} ${treating.length === 1 ? 'Condition' : 'Conditions'}`;
  const goalsTitle = `Working Toward: ${linked.length} ${linked.length === 1 ? 'Goal' : 'Goals'}`;

  return (
    <section
      data-testid="care-plan-card"
      className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
    >
      <div className="h-12 p-1">
        <button
          type="button"
          onClick={() => onToggle(token)}
          data-testid="care-plan-card-header"
          aria-expanded={expanded}
          className="flex size-full items-center gap-3 rounded-sm px-3 text-left hover:bg-bg-secondary"
        >
          <Text
            inline
            size="sm"
            weight="semibold"
            className="min-w-0 flex-1 truncate"
          >
            {plan.title ?? 'Care Plan'}
          </Text>
          <Icon
            name="ChevronDown"
            size="xs"
            className={cn(
              'shrink-0 text-text-tertiary transition-transform',
              !expanded && '-rotate-90',
            )}
          />
        </button>
      </div>

      {!expanded ? (
        <div>
          {(steps.length > 0 || progress) && (
            <div
              className={cn(
                'grid gap-6 px-4 pb-4',
                steps.length > 0 && progress ? 'grid-cols-2' : 'grid-cols-1',
              )}
            >
              {steps.length > 0 && (
                <div className="flex flex-col justify-between gap-2">
                  <Text
                    size="xs"
                    color="tertiary"
                    className="truncate text-center"
                  >
                    Phase:{' '}
                    {phaseNumber !== null && (
                      <>
                        <Text inline size="xs">
                          {phaseNumber} of {steps.length}
                        </Text>
                        {' · '}
                      </>
                    )}
                    {now && (
                      <Text
                        inline
                        size="xs"
                        weight="semibold"
                        className="text-text-indigo"
                      >
                        {now.title}
                      </Text>
                    )}
                  </Text>
                  <PhasePills steps={steps} />
                </div>
              )}
              {progress && (
                <div className="flex flex-col justify-between gap-2">
                  <Text size="xs" color="tertiary" className="text-center">
                    Week:{' '}
                    <Text inline size="xs">
                      {progress.weekNow} of {progress.weekTotal}
                    </Text>
                  </Text>
                  <ProgressBar percent={progress.percent} />
                </div>
              )}
            </div>
          )}
          {(treating.length > 0 || linked.length > 0) && (
            <>
              <div
                aria-hidden
                className="mx-4 border-t border-border-primary"
              />
              <div className="flex flex-col gap-3 p-1 pt-3">
                {treating.length > 0 && (
                  <SummaryRows
                    title={treatingTitle}
                    rows={treating.map((row) => ({
                      token: row.token,
                      display: row.display,
                      resolved: Boolean(row.condition),
                    }))}
                    testId="care-plan-condition-row"
                    onOpen={onOpenCondition}
                  />
                )}
                {linked.length > 0 && (
                  <SummaryRows
                    title={goalsTitle}
                    rows={linked.map((row) => ({
                      token: row.token,
                      display: row.display,
                      resolved: Boolean(row.goal),
                    }))}
                    testId="care-plan-goal-row"
                    onOpen={onOpenGoal}
                  />
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="pb-1">
          <div className="px-4">
            {steps.length > 0 && (
              <div className="pt-2">
                <PhaseStepper steps={steps} />
              </div>
            )}
            {(plan.period?.start ?? progress) && (
              <div className="mt-6 border-t border-border-primary pt-5 pb-4">
                <div
                  style={
                    steps.length > 0
                      ? {
                          marginInline: `calc(${100 / (2 * steps.length)}% - 0.6875rem)`,
                        }
                      : undefined
                  }
                >
                  <DatedProgress
                    started={plan.period?.start?.slice(0, 10)}
                    end={planEndDate(plan)}
                    weekNow={progress?.weekNow}
                    weekTotal={progress?.weekTotal}
                    percent={progress?.percent}
                  />
                </div>
              </div>
            )}
          </div>
          {(treating.length > 0 || linked.length > 0) && (
            <>
              <div
                aria-hidden
                className="mx-4 border-t border-border-primary"
              />
              <div className="flex flex-col gap-3 p-1 pt-3">
                {treating.length > 0 && (
                  <DetailSection title={treatingTitle}>
                    <ConditionDetailRows
                      conditions={treating}
                      onOpenCondition={onOpenCondition}
                    />
                  </DetailSection>
                )}
                {linked.length > 0 && (
                  <DetailSection title={goalsTitle}>
                    <GoalDetailRows
                      goals={linked}
                      grouped={grouped}
                      onOpenGoal={onOpenGoal}
                    />
                  </DetailSection>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};
